import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { FieldValues, FieldPath, RegisterOptions } from 'react-hook-form';
import { Tooltip } from '@heroui/react';
import type { TooltipProps } from '@heroui/react';
import { Icon } from '@iconify/react';
import { I18nMessage } from '../i18n/I18nMessage';

export interface FormItemProps {
    /** 字段名称 */
    name: string;
    /** 标签文本 */
    label?: React.ReactNode;
    /** 是否必填 */
    required?: boolean;
    /** 帮助文本 */
    help?: React.ReactNode;
    /** 错误消息 */
    errorMessage?: string;
    /** 布局方式 */
    layout?: 'vertical' | 'horizontal';
    /** 标签对齐方式 */
    labelAlign?: 'left' | 'right';
    /** 显示冒号 */
    colon?: boolean;
    /** 验证规则 */
    rules?: Omit<RegisterOptions<FieldValues, FieldPath<FieldValues>>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
    /** 默认值 */
    defaultValue?: unknown;
    /** 子组件 */
    children: React.ReactNode;
    /** 工具提示配置 */
    tooltip?: Omit<TooltipProps, "children">;
    /** 样式类名 */
    className?: string;
    /** 验证状态 */
    validateStatus?: 'success' | 'warning' | 'error' | 'validating';
    /** 是否可清空 */
    canClear?: boolean;
    /** 占位符 */
    placeholder?: string;
}

export function FormItem({
    name,
    label,
    required,
    help,
    tooltip,
    rules,
    defaultValue,
    errorMessage,
    layout = 'vertical',
    labelAlign = 'left',
    colon = true,
    children,
    className,
    validateStatus,
    canClear,
    placeholder,
}: FormItemProps) {
    const { control, formState: { errors }, clearErrors } = useFormContext();
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    const error = errors[name];
    const hasError = !!error;
    const isInvalid = hasError || validateStatus === 'error';

    const childElement = React.Children.only(children) as React.ReactElement;

    const combinedRules = {
        ...rules,
        required: required ? (rules?.required || `${label || name} 是必填项`) : false,
    };

    const inferredDefaultValue = defaultValue !== undefined ? defaultValue : getDefaultValueByType(childElement);
    const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);

    const renderLabel = () => {
        if (!label) return null;

        return (
            <div className={`form-item-label ${labelAlign === 'right' ? 'text-right' : 'text-left'}`}>
                <label className="text-sm font-medium text-foreground">
                    {typeof label === 'string' ? (
                        <I18nMessage id={label} defaultValue={label} />
                    ) : (
                        label
                    )}
                    {required && <span className="text-danger ml-1">*</span>}
                    {colon && <span className="ml-1">:</span>}
                </label>
                {tooltip && (
                    <Tooltip {...tooltip}>
                        <Icon
                            icon="solar:info-circle-line-duotone"
                            className="ml-2 text-default-400 cursor-help"
                        />
                    </Tooltip>
                )}
            </div>
        );
    };

    const renderHelp = () => {
        if (!help) return null;

        return (
            <div className="form-item-help text-xs text-default-500 mt-1">
                {typeof help === 'string' ? (
                    <I18nMessage id={help} defaultValue={help} />
                ) : (
                    help
                )}
            </div>
        );
    };

    const renderErrorMessage = () => {
        if (!hasError && !errorMessage) return null;

        const message = errorMessage || (error as { message?: string })?.message || 'Validation error';

        return (
            <div className="form-item-error text-xs text-danger mt-1">
                <Icon icon="solar:danger-circle-line-duotone" className="inline mr-1" />
                {message}
            </div>
        );
    };

    const renderValidationIcon = () => {
        if (!validateStatus) return null;

        const iconMap = {
            success: { icon: 'solar:check-circle-line-duotone', className: 'text-success' },
            warning: { icon: 'solar:danger-triangle-line-duotone', className: 'text-warning' },
            error: { icon: 'solar:close-circle-line-duotone', className: 'text-danger' },
            validating: { icon: 'solar:refresh-circle-line-duotone', className: 'text-default-400 animate-spin' },
        };

        const config = iconMap[validateStatus];
        if (!config) return null;

        return (
            <Icon
                icon={config.icon}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${config.className}`}
            />
        );
    };

    const enhanceChild = (child: React.ReactElement, value: unknown, onChange: (value: unknown) => void) => {
        const baseProps = {
            value,
            onChange,
            name,
            isInvalid,
            ...(canClear && hasOnClear(child) && { onClear: () => clearErrors(name) }),
            ...(placeholder && supportsPlaceholder(child) && { placeholder }),
        };

        // 处理支持 isRequired 的组件
        if (required && supportsIsRequired(child)) {
            (baseProps as Record<string, unknown>).isRequired = true;
        }

        // 处理支持 errorMessage 的组件
        if (hasError && supportsErrorMessage(child)) {
            (baseProps as Record<string, unknown>).errorMessage = (error as { message?: string })?.message || 'Validation error';
        }

        // 处理密码显示切换
        if (supportsPasswordToggle(child)) {
            (baseProps as Record<string, unknown>).type = isPasswordVisible ? 'text' : 'password';
            (baseProps as Record<string, unknown>).endContent = (
                <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="text-default-400 hover:text-default-600"
                >
                    <Icon icon={isPasswordVisible ? 'solar:eye-closed-line-duotone' : 'solar:eye-line-duotone'} />
                </button>
            );
        }

        return React.cloneElement(child, { ...baseProps });
    };

    const renderFormItem = () => {
        const labelElement = renderLabel();
        const helpElement = renderHelp();
        const errorElement = renderErrorMessage();
        const validationIcon = renderValidationIcon();

        if (layout === 'horizontal') {
            return (
                <div className={`form-item-horizontal flex items-start gap-4 ${className || ''}`}>
                    {labelElement && (
                        <div className="form-item-label-col flex-shrink-0 w-1/4">
                            {labelElement}
                        </div>
                    )}
                    <div className="form-item-wrapper-col flex-1 w-3/4">
                        <div className="form-item-content relative">
                            <Controller
                                name={name}
                                control={control}
                                rules={combinedRules}
                                defaultValue={inferredDefaultValue}
                                render={({ field: { value, onChange } }) =>
                                    enhanceChild(childElement, value, onChange)
                                }
                            />
                            {validationIcon}
                        </div>
                        {helpElement}
                        {errorElement}
                    </div>
                </div>
            );
        }

        return (
            <div className={`form-item-vertical space-y-2 ${className || ''}`}>
                {labelElement}
                <div className="form-item-content relative">
                    <Controller
                        name={name}
                        control={control}
                        rules={combinedRules}
                        defaultValue={inferredDefaultValue}
                        render={({ field: { value, onChange } }) =>
                            enhanceChild(childElement, value, onChange)
                        }
                    />
                    {validationIcon}
                </div>
                {helpElement}
                {errorElement}
            </div>
        );
    };

    return renderFormItem();
}

function getDefaultValueByType(child: React.ReactElement): unknown {
    const componentType = child.type;
    const componentName = typeof componentType === 'string' ? componentType :
        (componentType as { displayName?: string; name?: string }).displayName ||
        (componentType as { displayName?: string; name?: string }).name ||
        'Unknown';

    // 根据组件类型推断默认值
    switch (componentName) {
        case 'Switch':
        case 'Checkbox':
            return false;
        case 'CheckboxGroup':
        case 'Select':
            return [];
        case 'Slider':
            return 0;
        case 'Input':
        case 'Textarea':
        case 'Autocomplete':
        default:
            return '';
    }
}

function hasOnClear(child: React.ReactElement): boolean {
    const componentType = child.type;
    const componentName = typeof componentType === 'string' ? componentType :
        (componentType as { displayName?: string; name?: string }).displayName ||
        (componentType as { displayName?: string; name?: string }).name ||
        'Unknown';

    return ['Input', 'Textarea', 'Autocomplete', 'Select'].includes(componentName);
}

function supportsPasswordToggle(child: React.ReactElement): boolean {
    const componentType = child.type;
    const componentName = typeof componentType === 'string' ? componentType :
        (componentType as { displayName?: string; name?: string }).displayName ||
        (componentType as { displayName?: string; name?: string }).name ||
        'Unknown';

    if (componentName !== 'Input') return false;
    if (!child.props || typeof child.props !== 'object') return false;

    const props = child.props as { type?: string };
    return props.type === 'password';
}

function supportsIsRequired(child: React.ReactNode): boolean {
    if (!React.isValidElement(child)) return false;

    const componentType = child.type;
    const componentName = typeof componentType === 'string' ? componentType :
        (componentType as { displayName?: string; name?: string }).displayName ||
        (componentType as { displayName?: string; name?: string }).name ||
        'Unknown';

    const supportedComponents = [
        'Input', 'Textarea', 'Select', 'Autocomplete', 'CheckboxGroup',
        'RadioGroup', 'DatePicker', 'TimeInput'
    ];

    return supportedComponents.includes(componentName);
}

function supportsErrorMessage(child: React.ReactNode): boolean {
    if (!React.isValidElement(child)) return false;

    const componentType = child.type;
    const componentName = typeof componentType === 'string' ? componentType :
        (componentType as { displayName?: string; name?: string }).displayName ||
        (componentType as { displayName?: string; name?: string }).name ||
        'Unknown';

    const supportedComponents = [
        'Input', 'Textarea', 'Select', 'Autocomplete', 'CheckboxGroup',
        'RadioGroup', 'DatePicker', 'TimeInput'
    ];

    return supportedComponents.includes(componentName);
}

function supportsPlaceholder(child: React.ReactElement): boolean {
    const componentType = child.type;
    const componentName = typeof componentType === 'string' ? componentType :
        (componentType as { displayName?: string; name?: string }).displayName ||
        (componentType as { displayName?: string; name?: string }).name ||
        'Unknown';

    return ['Input', 'Textarea', 'Autocomplete', 'Select'].includes(componentName);
} 